// Company address book (VERA-871) — fulfillment locations.

import type { ExampleKind } from '@repo/types'

/**
 * Kept in step with the API's `ADDRESS_TYPES`
 * (`apps/api/src/domain/types/address/address.interface.ts`) and the Postgres enum
 * `enum_address_type`. It is a fourth copy rather than an import because
 * `apps/platform` does not take a runtime dependency on the API's domain types — so
 * `address-type-allowlist.guard.spec.ts` asserts the keys of `ADDRESS_TYPE_LABELS`
 * below match the other two lists, and fails if this file drifts from them.
 */
export type AddressType = 'office' | 'store' | 'production' | 'distributor' | 'other'

export type Address = {
  id: string
  company_id: string
  type: AddressType
  label: string | null
  // Lucide icon key for the "Custom" type (type === 'other'); null otherwise.
  icon?: string | null
  full_name: string
  country: string
  state: string | null
  city: string
  postal_code: string
  address_line: string
  // Geocoding from Google Places (feeds the analytics scan map).
  latitude?: number | null
  longitude?: number | null
  place_id?: string | null
  formatted_address?: string | null
  /**
   * VERA-931 — named on the PUBLIC / owner-facing chain of custody? `null` =
   * decide by type (store yes, everything else no). The brand's own admin
   * provenance always shows every hop.
   */
  show_in_provenance?: boolean | null
  is_default: boolean
  // VRG-512 — fenced sample rows. Null for every real, brand-authored address.
  example_kind?: ExampleKind | null
  created_at?: string
  updated_at?: string | null
}

// Shape posted/put to the API for create + edit.
export type AddressPayload = {
  type: AddressType
  label?: string | null
  icon?: string | null
  full_name: string
  country: string
  state?: string | null
  city: string
  postal_code: string
  address_line: string
  latitude?: number | null
  longitude?: number | null
  place_id?: string | null
  formatted_address?: string | null
  is_default?: boolean
}

// Declared in the same order as the API allow-list and the database enum, and asserted
// to be so by `address-type-allowlist.guard.spec.ts` — 'other' ("Custom") stays last
// because it is the catch-all, so a concrete type must never be added after it.
export const ADDRESS_TYPE_LABELS: Record<AddressType, string> = {
  office: 'Office',
  store: 'Store',
  production: 'Production',
  // A wholesaler or regional distributor — the hop between production and store.
  distributor: 'Distributor',
  // The 'other' token is surfaced as "Custom" — a user-named, user-iconed address.
  other: 'Custom',
}
